import { useCallback, useEffect, useState } from 'react';
import {
    ContourRedoRequestedEvent,
    ContourUndoRequestedEvent,
    domainEventBus,
} from '~/domain/events';
import { getContourUndoService } from '~/domain/ServiceContainer';
import {
    ContourHistoryEntry,
    ContourJump,
    ContourOperationResult,
} from '~/domain/undo';

/**
 * Announces a restored snapshot, passing the restore through so callers can
 * both act on it and return it. A missing result means the service declined
 * the move; nothing is published for it.
 */
const publishRestore = (
    result: ContourOperationResult | undefined,
    EventClass:
        | typeof ContourUndoRequestedEvent
        | typeof ContourRedoRequestedEvent
): ContourOperationResult | undefined => {
    if (!result) {
        return undefined;
    }
    const event = new EventClass(
        result.structureSetUid,
        result.roiNumber,
        result.compressedPixels,
        result.grid
    );
    domainEventBus.publish(event).catch((error) => {
        console.error('Failed to publish contour event:', error);
    });
    return result;
};

/**
 * One recorded step of one ROI's contour history, as it can be recognised
 * again later. Its position in the stack is deliberately not part of it:
 * the stack evicts intermediate steps, which renumbers everything after
 * them.
 */
export interface ContourEditStep {
    /** The operation label the step was recorded under. */
    readonly operation: string;
    readonly recordedAtMs: number;
}

/**
 * The step an ROI's contour currently reflects, or undefined when it has no
 * history. Called immediately after a write, it names that write's own step
 * for {@link undoContourEditIfCurrent}.
 */
export const captureCurrentContourEdit = (
    structureSetUid: string,
    roiNumber: number
): ContourEditStep | undefined => {
    const current = getContourUndoService()
        .getHistory(structureSetUid, roiNumber)
        .find((entry) => entry.isCurrent);
    return current
        ? {
              operation: current.operation,
              recordedAtMs: current.timestamp.getTime(),
          }
        : undefined;
};

/**
 * How long {@link captureRecordedContourEdit} waits for the write's entry
 * before giving up. Generous: the wait is for one timer hop plus the
 * handler's awaits, and the cost of expiring early is an Undo that
 * declines an edit it could have reverted.
 */
const STEP_RECORDED_TIMEOUT_MS = 5000;

const isSameStep = (
    a: ContourEditStep | undefined,
    b: ContourEditStep | undefined
): boolean =>
    a?.operation === b?.operation && a?.recordedAtMs === b?.recordedAtMs;

/**
 * The step a write records, resolved once it actually exists in history.
 *
 * The entry is NOT there when the write returns: the ROI publishes its
 * change on a timer and the handler awaits a snapshot before recording it.
 * Reading the history synchronously therefore names the PREVIOUS step, so
 * an offer built on it either declines an edit it should revert or, once
 * the user has undone their way back, reverts a different edit than the one
 * it is labelled with.
 *
 * Call BEFORE the write so the baseline is the step it supersedes.
 * Resolves undefined if nothing is recorded within the timeout — the offer
 * then declines, which is the safe direction.
 */
export const captureRecordedContourEdit = (
    structureSetUid: string,
    roiNumber: number
): Promise<ContourEditStep | undefined> => {
    const baseline = captureCurrentContourEdit(structureSetUid, roiNumber);
    return new Promise((resolve) => {
        // A holder, because `subscribe` invokes its listener before
        // returning: on that first call the handle it will return does not
        // exist yet, and the listener has to be able to reach it later.
        const pending: {
            subscription?: { unsubscribe: () => void };
            timer?: ReturnType<typeof setTimeout>;
            settled: boolean;
        } = { settled: false };

        const settle = (step: ContourEditStep | undefined) => {
            if (pending.settled) {
                return;
            }
            pending.settled = true;
            if (pending.timer !== undefined) {
                clearTimeout(pending.timer);
            }
            queueMicrotask(() => pending.subscription?.unsubscribe());
            resolve(step);
        };

        const check = () => {
            const entry = getContourUndoService()
                .getHistory(structureSetUid, roiNumber)
                .find((candidate) => candidate.isCurrent);
            // An ROI edited for the first time this session records the
            // state BEFORE the edit as its own entry, and that lands first.
            // Settling on it would name a step the write is not, and the
            // offer would then decline the very deletion it is attached to.
            if (!entry || entry.isInitial) {
                return;
            }
            const current = {
                operation: entry.operation,
                recordedAtMs: entry.timestamp.getTime(),
            };
            if (!isSameStep(current, baseline)) {
                settle(current);
            }
        };

        pending.timer = setTimeout(
            () => settle(undefined),
            STEP_RECORDED_TIMEOUT_MS
        );
        // Never rejects: the caller starts this and does not await it, so a
        // throw here would surface as an unhandled rejection instead of the
        // declined offer that not knowing the step actually means.
        try {
            pending.subscription = getContourUndoService().subscribe(check);
            if (pending.settled) {
                pending.subscription.unsubscribe();
            }
        } catch (error) {
            console.error('Capturing the contour edit step failed:', error);
            settle(undefined);
        }
    });
};

/**
 * Undo `step` — and only `step`. The ROI is addressed explicitly rather
 * than through the current selection, and the step explicitly rather than
 * through "whatever is newest".
 *
 * For an affordance that outlives the edit it names: an Undo offered on a
 * notification stays on screen while the user carries on editing, and the
 * plain undo it would otherwise perform reverts whichever edit is newest by
 * then — a different edit from the one the control is labelled with.
 * Declines instead, so the caller can say the offer has lapsed.
 *
 * @returns whether the ROI was reverted.
 */
export const undoContourEditIfCurrent = (
    structureSetUid: string,
    roiNumber: number,
    step: ContourEditStep
): boolean => {
    const current = captureCurrentContourEdit(structureSetUid, roiNumber);
    if (
        !current ||
        current.operation !== step.operation ||
        current.recordedAtMs !== step.recordedAtMs
    ) {
        return false;
    }
    return (
        publishRestore(
            getContourUndoService().undo(structureSetUid, roiNumber),
            ContourUndoRequestedEvent
        ) !== undefined
    );
};

/**
 * A jump can move the pointer either way, and the two restore events are
 * handled identically downstream — but listeners keyed on 'undo' vs 'redo'
 * (the difference overlay, the 3D preview) would read a backwards jump
 * announced as a redo as movement in the wrong direction.
 */
const publishJump = (
    jump: ContourJump | undefined
): ContourJump | undefined => {
    if (jump) {
        publishRestore(
            jump.result,
            jump.direction === 'backward'
                ? ContourUndoRequestedEvent
                : ContourRedoRequestedEvent
        );
    }
    return jump;
};

export interface UndoRedoState {
    canUndo: boolean;
    canRedo: boolean;
    /** True when the ROI has recorded edits that a revert would discard. */
    canRevertToInitial: boolean;
    /** Every retained step, oldest first. */
    history: ContourHistoryEntry[];
    /**
     * Intermediate steps dropped to keep the stack within its size cap. When
     * non-zero, {@link history} has a gap between the initial state and the
     * oldest retained edit.
     */
    discardedStepCount: number;
}

const EMPTY_STATE: UndoRedoState = {
    canUndo: false,
    canRedo: false,
    canRevertToInitial: false,
    history: [],
    discardedStepCount: 0,
};

/**
 * Undo/redo state and actions for one ROI of one structure set.
 *
 * @param structureSetUid UID of the set the ROI belongs to. Undo history is
 *   scoped to it, because ROINumbers restart at 1 in every set and the stacks
 *   outlive a set switch — without the UID, selecting ROI 1 in a second set
 *   would show the first set's history and undo would restore its pixels.
 *   Undefined (no set loaded) yields empty state and inert actions.
 */
export const useUndoRedo = (
    structureSetUid: string | undefined,
    roiNumber: number
) => {
    const [state, setState] = useState<UndoRedoState>(EMPTY_STATE);

    useEffect(() => {
        if (!structureSetUid) {
            setState(EMPTY_STATE);
            return;
        }
        const contourUndoService = getContourUndoService();
        const updateState = () => {
            setState({
                canUndo: contourUndoService.canUndo(structureSetUid, roiNumber),
                canRedo: contourUndoService.canRedo(structureSetUid, roiNumber),
                canRevertToInitial: contourUndoService.canRevertToInitial(
                    structureSetUid,
                    roiNumber
                ),
                history: contourUndoService.getHistory(
                    structureSetUid,
                    roiNumber
                ),
                discardedStepCount: contourUndoService.getDiscardedStepCount(
                    structureSetUid,
                    roiNumber
                ),
            });
        };
        const subscription = contourUndoService.subscribe(updateState);
        return subscription.unsubscribe;
    }, [structureSetUid, roiNumber]);

    const undo = () =>
        structureSetUid
            ? publishRestore(
                  getContourUndoService().undo(structureSetUid, roiNumber),
                  ContourUndoRequestedEvent
              )
            : undefined;

    const redo = () =>
        structureSetUid
            ? publishRestore(
                  getContourUndoService().redo(structureSetUid, roiNumber),
                  ContourRedoRequestedEvent
              )
            : undefined;

    /** Restore the ROI to a step from {@link UndoRedoState.history}. */
    const jumpTo = useCallback(
        (index: number) =>
            structureSetUid
                ? publishJump(
                      getContourUndoService().jumpTo(
                          structureSetUid,
                          roiNumber,
                          index
                      )
                  )
                : undefined,
        [structureSetUid, roiNumber]
    );

    /** Restore the ROI to its state before this session's first edit. */
    const revertToInitial = useCallback(
        () =>
            structureSetUid
                ? publishJump(
                      getContourUndoService().revertToInitial(
                          structureSetUid,
                          roiNumber
                      )
                  )
                : undefined,
        [structureSetUid, roiNumber]
    );

    return { ...state, undo, redo, jumpTo, revertToInitial };
};
